/**
 * ProfileCompletionModal
 *
 * Aparece quando o usuário logado não tem gender ou birth_date preenchidos.
 * Pode ser pulado ("Preencher depois") — voltará na próxima sessão.
 */
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { todayLocalISO } from "../utils/date";
import { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./ProfileCompletionModal.module.css";
import ModalHeader from "./ModalHeader";
import SplitDateInput from "./SplitDateInput";

const GENDER_OPTIONS = [
  { value: "masculino",           label: "Masculino" },
  { value: "feminino",            label: "Feminino" },
  { value: "outro",               label: "Outro" },
];

const ACTIVITY_OPTIONS = [
  { value: "sedentario",  label: "Sedentário",         desc: "Pouco ou nenhum exercício" },
  { value: "leve",        label: "Levemente ativo",    desc: "Exercício 1–3×/semana" },
  { value: "moderado",    label: "Moderadamente ativo",desc: "Exercício 3–5×/semana" },
  { value: "ativo",       label: "Muito ativo",        desc: "Exercício 6–7×/semana" },
  { value: "muito_ativo", label: "Extremamente ativo", desc: "Treino 2×/dia ou trabalho físico" },
];

export default function ProfileCompletionModal({ user, onSaved, onSkip }) {
  useBodyScrollLock();
  const [gender, setGender]           = useState(user?.gender || "");
  const [birthDate, setBirthDate]     = useState(user?.birth_date || "");
  const [heightCm, setHeightCm]       = useState(user?.height_cm ? String(user.height_cm) : "");
  const [activityLevel, setActivity]  = useState(user?.activity_level || "");
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [canScrollMore, setCanScrollMore] = useState(false);

  const canSave = gender && birthDate && heightCm && activityLevel;

  // M1 — Swipe-to-dismiss (bottom sheet, mobile only)
  const modalRef = useRef(null);
  const bodyRef  = useRef(null);
  const dragRef  = useRef({ startY: 0, lastY: 0, active: false });

  const onTouchStart = useCallback((e) => {
    if (window.innerWidth >= 600) return;
    dragRef.current = { startY: e.touches[0].clientY, lastY: e.touches[0].clientY, active: true };
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!dragRef.current.active || window.innerWidth >= 600) return;
    const body = bodyRef.current;
    const deltaY = e.touches[0].clientY - dragRef.current.startY;
    // Only intercept downward swipe when body scroll is at the top
    if (deltaY > 0 && (!body || body.scrollTop === 0)) {
      dragRef.current.lastY = e.touches[0].clientY;
      if (modalRef.current) {
        modalRef.current.style.transition = "none";
        modalRef.current.style.transform  = `translateY(${deltaY}px)`;
      }
      if (e.cancelable) e.preventDefault();
    } else {
      // Let normal scroll happen
      dragRef.current.active = false;
      if (modalRef.current) {
        modalRef.current.style.transform  = "";
        modalRef.current.style.transition = "";
      }
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    const deltaY = dragRef.current.lastY - dragRef.current.startY;
    const modal  = modalRef.current;
    if (!modal) return;
    if (deltaY > 110) {
      // Dismiss
      modal.style.transition = "transform 0.22s ease-out";
      modal.style.transform  = "translateY(100%)";
      setTimeout(onSkip, 220);
    } else {
      // Snap back
      modal.style.transition = "transform 0.28s cubic-bezier(0.34,1.56,0.64,1)";
      modal.style.transform  = "";
      setTimeout(() => { if (modal) modal.style.transition = ""; }, 280);
    }
  }, [onSkip]);

  // M2 — Scroll indicator
  const updateScrollFade = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    setCanScrollMore(el.scrollHeight - el.scrollTop - el.clientHeight > 10);
  }, []);

  useEffect(() => {
    updateScrollFade();
  }, [updateScrollFade, gender, birthDate, heightCm, activityLevel]);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("gender", gender);
      fd.append("birth_date", birthDate);
      fd.append("height_cm", heightCm);
      fd.append("activity_level", activityLevel);
      const res = await api.put("/users/me/profile", fd);
      onSaved?.(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div
        className={styles.modal}
        ref={modalRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Drag handle — visible on mobile only */}
        <div className={styles.dragHandle} aria-hidden="true" />
        <ModalHeader onClose={onSkip} />

        {/* Scrollable body */}
        <div className={styles.body} ref={bodyRef} onScroll={updateScrollFade}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.iconWrap}>
              <FontAwesomeIcon icon={faUser} />
            </div>
            <div>
              <h2 className={styles.title}>Complete seu perfil</h2>
              <p className={styles.subtitle}>
                Essas informações ajudam a personalizar melhor seu treino.
              </p>
            </div>
          </div>

          {/* Gênero */}
          <div className={styles.field}>
            <label className={styles.label}>Gênero</label>
            <div className={styles.chipGroup}>
              {GENDER_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`${styles.chip} ${gender === opt.value ? styles.chipActive : ""}`}
                  onClick={() => setGender(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Data de nascimento */}
          <div className={styles.field}>
            <label className={styles.label}>Data de nascimento</label>
            <SplitDateInput
              value={birthDate}
              onChange={setBirthDate}
              inputClassName={styles.dateInput}
              maxDate={todayLocalISO()}
            />
          </div>

          {/* Altura */}
          <div className={styles.field}>
            <label className={styles.label}>Altura (cm)</label>
            <input
              type="number"
              className={styles.dateInput}
              placeholder="Ex: 175"
              min={50}
              max={250}
              value={heightCm}
              onChange={e => setHeightCm(e.target.value)}
            />
          </div>

          {/* Nível de atividade */}
          <div className={styles.field}>
            <label className={styles.label}>Nível de atividade</label>
            <div className={styles.activityGroup}>
              {ACTIVITY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`${styles.activityChip} ${activityLevel === opt.value ? styles.chipActive : ""}`}
                  onClick={() => setActivity(opt.value)}
                >
                  <span className={styles.activityLabel}>{opt.label}</span>
                  <span className={styles.activityDesc}>{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {error && <p className={styles.error}>{error}</p>}
          {/* M2 — scroll fade gradient */}
          {canScrollMore && <div className={styles.scrollFade} aria-hidden="true" />}
        </div>

        {/* Ações — sticky footer */}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.skipBtn}
            onClick={onSkip}
            disabled={saving}
          >
            Preencher depois
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            {saving ? "Salvando..." : (
              <>Salvar <FontAwesomeIcon icon={faChevronRight} style={{ marginLeft: 6, fontSize: 12 }} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
